import { useAuthStore } from "@/stores/authStore"
import { decideRouteAccess } from "~/utils/authNavigation"

/**
 * The one gate every navigation passes through. It does the ASYNC work —
 * restore a session from a stored token, sign out a SysAdmin — then hands
 * the resolved state to `decideRouteAccess` (a pure, tested function in
 * utils/authNavigation.js) and executes its verdict. All the branching
 * RULES live there; this file is just the orchestration + the navigations.
 */
export default defineNuxtRouteMiddleware(async to => {
  // Server render doesn't have the client's token storage — the client
  // plugin handles the initial restore.
  if (import.meta.server) return

  const auth = useAuthStore()
  if (!auth.isInitialized) auth.init()

  // ── Resolve the real auth state ──────────────────────────────────────────
  let isAuthenticated = auth.isAuthenticated

  // A stored token but no user in memory = a page (re)load — restore it.
  if (!isAuthenticated && auth.sessionToken) {
    try {
      const { success } = await auth.fetchAuthenticatedUser()

      isAuthenticated = success
      if (!success) auth.clearAuth()
    } catch (err) {
      console.error("[auth.middleware] session restore failed:", err)
      auth.clearAuth()
      isAuthenticated = false
    }
  }

  // ── Decide, then act ─────────────────────────────────────────────────────
  const verdict = decideRouteAccess({
    isAuthenticated,
    role: auth.authenticatedUser?.role ?? null,
    meta: to.meta ?? {},
    hasPermission: p => auth.hasPermission(p),
    fullPath: to.fullPath,
    redirectQuery: typeof to.query?.redirect === "string" ? to.query.redirect : undefined,
  })

  switch (verdict.type) {
  case "signout-allow":
    // A SysAdmin on a login-type page: sign the technical account out, but
    // there's nowhere to bounce them — the page they're on is fine.
    await signOut(auth)
    
    return

  case "signout-then-login":
    await signOut(auth)
    
    return toLogin(verdict.redirect)

  case "login":
    return toLogin(verdict.redirect)

  case "redirect":
    return navigateTo(verdict.to, { replace: true })

  case "unauthorized":
    return navigateTo(
      {
        path: "/account/unauthorized",

        // Surface WHY (which role or permission was expected) so the
        // unauthorized screen can explain the landing.
        query: {
          from: verdict.from,
          ...(verdict.role ? { role: verdict.role } : {}),
          ...(verdict.permission ? { permission: verdict.permission } : {}),
        },
      },
      { replace: true },
    )

  default:

    // "allow" — let the navigation proceed.
  }
})

/** Sign out the technical (SysAdmin) account, tolerating any failure. */
async function signOut(auth) {
  try {
    await auth.logout()
  } catch {
    auth.clearAuth()
  }
}

/** Send to the login page, carrying the path the user wanted to reach. */
function toLogin(redirect) {
  return navigateTo(
    { path: "/account/login", query: { redirect } },
    { replace: true },
  )
}
